import React, { useState } from 'react';
import {
  Lock,
  ShieldCheck,
  LogOut,
  Loader2,
  X,
  Clock,
  Hash,
  MessageSquare,
  AlertTriangle,
  ChevronRight,
  ThumbsUp,
  ThumbsDown,
  Heart,
  MessageCircle,
  Send,
  Bookmark,
  MoreHorizontal,
  Image as ImageIcon,
} from 'lucide-react';
import { ScheduledPost, approvePost, rejectPost } from '@/lib/brandStudio';
import { activeBrand } from '@/lib/adminBrand';

import { typeColors } from './PostEditor';
import {
  isApprovalAdmin,
  loginApprovalAdmin,
  logoutApprovalAdmin,
  APPROVAL_ADMIN_USERNAME,
} from '@/lib/approvalAdmin';

type Step = 1 | 2 | 3 | 4 | 5;

interface ApprovalStepProps {
  posts: ScheduledPost[];
  refresh: () => Promise<void>;
  fmtDateTime: (iso: string | null) => string;
  goToStep: (s: Step) => void;
}

const statusBadge: Record<string, string> = {
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  rejected: 'bg-red-50 text-red-700 border-red-300',
  pending: 'bg-amber-50 text-amber-700 border-amber-300',
};

const ApprovalStep: React.FC<ApprovalStepProps> = ({ posts, refresh, fmtDateTime, goToStep }) => {
  const [authed, setAuthed] = useState(isApprovalAdmin());

  if (!authed) {
    return <AdminLoginGate onSuccess={() => setAuthed(true)} />;
  }

  return (
    <ApprovalBoard
      posts={posts}
      refresh={refresh}
      fmtDateTime={fmtDateTime}
      goToStep={goToStep}
      onLogout={() => {
        logoutApprovalAdmin();
        setAuthed(false);
      }}
    />
  );
};

// -------- Login gate --------

const AdminLoginGate: React.FC<{ onSuccess: () => void }> = ({ onSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    setTimeout(() => {
      if (loginApprovalAdmin(username.trim(), password)) {
        onSuccess();
      } else {
        setError('Invalid administrator credentials.');
      }
      setBusy(false);
    }, 300);
  };

  return (
    <div className="max-w-md mx-auto py-10">
      <div className="text-center mb-6">
        <div className="w-14 h-14 mx-auto rounded-full bg-[#0D1B2A] flex items-center justify-center mb-3">
          <Lock className="w-6 h-6 text-[#C9A961]" />
        </div>
        <h3 className="font-serif text-2xl text-[#0D1B2A] font-light">Step 4 · Administrator Approval</h3>
        <p className="font-sans text-sm text-slate-600 mt-2">
          This area is restricted. Sign in to review, approve, or send posts back for revision.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="border border-slate-200 bg-white p-6 space-y-4">
        {error && (
          <div className="flex items-center gap-2 border border-red-300 bg-red-50 px-3 py-2">
            <X className="w-4 h-4 text-red-500" />
            <span className="font-sans text-xs text-red-600">{error}</span>
          </div>
        )}
        <div>
          <label className="font-sans text-[10px] tracking-[0.2em] uppercase text-slate-400">Username</label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            className="mt-1 w-full border border-slate-200 px-3 py-2 font-sans text-sm bg-white text-[#0D1B2A] placeholder:text-slate-400 focus:border-[#C9A961] outline-none"
            placeholder="Administrator username"
          />
        </div>
        <div>
          <label className="font-sans text-[10px] tracking-[0.2em] uppercase text-slate-400">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            className="mt-1 w-full border border-slate-200 px-3 py-2 font-sans text-sm bg-white text-[#0D1B2A] placeholder:text-slate-400 focus:border-[#C9A961] outline-none"
            placeholder="••••••••"
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          className="w-full font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-3 bg-[#C9A961] text-[#0D1B2A] hover:bg-[#b89651] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          Sign In to Review
        </button>
      </form>
    </div>
  );
};

// -------- Approval board --------

interface BoardProps extends ApprovalStepProps {
  onLogout: () => void;
}

const ApprovalBoard: React.FC<BoardProps> = ({ posts, refresh, fmtDateTime, goToStep, onLogout }) => {
  const [filter, setFilter] = useState<'pending' | 'approved' | 'rejected' | 'all'>('pending');
  const [feedbackDraft, setFeedbackDraft] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  const counts = {
    pending: posts.filter((p) => (p.approval_status || 'pending') === 'pending').length,
    approved: posts.filter((p) => p.approval_status === 'approved').length,
    rejected: posts.filter((p) => p.approval_status === 'rejected').length,
  };

  const filtered = posts.filter((p) => {
    const st = p.approval_status || 'pending';
    if (filter === 'all') return true;
    return st === filter;
  });

  const handleApprove = async (p: ScheduledPost) => {
    setBusyId(p.id);
    await approvePost(p.id, APPROVAL_ADMIN_USERNAME, feedbackDraft[p.id]);
    await refresh();
    setBusyId(null);
  };

  const handleReject = async (p: ScheduledPost) => {
    const fb = (feedbackDraft[p.id] || '').trim();
    if (!fb) {
      alert('Please add feedback explaining why this post is being sent back.');
      return;
    }
    setBusyId(p.id);
    await rejectPost(p.id, APPROVAL_ADMIN_USERNAME, fb);
    await refresh();
    setBusyId(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-emerald-700">
              Signed in as {APPROVAL_ADMIN_USERNAME}
            </span>
          </div>
          <h3 className="font-serif text-2xl text-[#0D1B2A] font-light mt-1">Step 4 · Approve Generated Posts</h3>
          <p className="font-sans text-sm text-slate-600 mt-2 max-w-2xl">
            Approve posts to advance them, or disapprove with feedback to send them back to Step 3 for revision.
          </p>
        </div>
        <button
          onClick={onLogout}
          className="font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-2 border border-slate-200 text-slate-600 hover:border-red-300 hover:text-red-500 transition-colors flex items-center gap-2"
        >
          <LogOut className="w-3.5 h-3.5" /> Sign Out
        </button>
      </div>

      {/* Stat tiles / filters */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {([
          { key: 'pending', label: 'Awaiting Review', value: counts.pending, color: 'text-amber-700' },
          { key: 'approved', label: 'Approved', value: counts.approved, color: 'text-emerald-700' },
          { key: 'rejected', label: 'Sent Back', value: counts.rejected, color: 'text-red-600' },
          { key: 'all', label: 'All Posts', value: posts.length, color: 'text-[#0D1B2A]' },
        ] as const).map((t) => (
          <button
            key={t.key}
            onClick={() => setFilter(t.key)}
            className={`text-left border p-4 transition-all ${
              filter === t.key ? 'border-[#C9A961] bg-[#C9A961]/5' : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
          >
            <div className={`font-serif text-2xl ${t.color}`}>{t.value}</div>
            <div className="font-sans text-[10px] tracking-[0.15em] uppercase text-slate-500 mt-1">{t.label}</div>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-slate-200 py-16 text-center bg-slate-50">
          <ThumbsUp className="w-8 h-8 mx-auto text-slate-300 mb-3" />
          <p className="font-sans text-sm text-slate-400">
            {filter === 'pending' ? 'Nothing awaiting review. All caught up.' : 'No posts in this view.'}
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {filtered.map((p, index) => {
            const st = p.approval_status || 'pending';
            const busy = busyId === p.id;
            const captionFirstLine = (p.caption || '').split('\n')[0];
            return (
              <div key={p.id} className="border border-slate-200 bg-white shadow-sm shadow-[#1A3B5C]/5 overflow-hidden">
                {/* Card header — identical to Step 3 Post Editor */}
                <div className="flex items-center gap-3 flex-wrap px-4 sm:px-5 py-3 bg-[#FAF8F3] border-b border-[#C9A961]/25">
                  <div className="w-7 h-7 rounded-full bg-[#0D1B2A] text-[#C9A961] font-serif text-sm flex items-center justify-center flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="min-w-0">
                    <div className="font-serif text-lg text-[#1A3B5C] leading-tight truncate">
                      {p.day_label || 'Post'}
                      <span className="font-sans text-[10px] text-slate-400 ml-2 tracking-wider uppercase">
                        {index + 1} of {filtered.length}
                      </span>
                    </div>
                    <div className="font-sans text-[11px] text-slate-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {fmtDateTime(p.scheduled_for)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-auto flex-wrap">
                    <span
                      className={`px-3 py-1 border text-[10px] tracking-[0.15em] uppercase font-sans rounded-full ${
                        statusBadge[st]
                      }`}
                    >
                      {st === 'pending' ? 'Awaiting Review' : st === 'rejected' ? 'Sent Back' : 'Approved'}
                    </span>
                    <span
                      className={`hidden sm:inline-flex px-3 py-1 border text-[10px] tracking-[0.15em] uppercase font-sans ${
                        typeColors[p.post_type] || typeColors['Feed Post']
                      }`}
                    >
                      {p.post_type}
                    </span>
                  </div>
                </div>

                <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 items-start p-4 sm:p-6">
                  {/* Instagram mockup — identical to Step 3 Post Editor */}
                  <div className="lg:sticky lg:top-28">
                    <div className="bg-white rounded-2xl shadow-xl shadow-[#1A3B5C]/10 overflow-hidden border border-[#C9A961]/20 max-w-[460px] mx-auto">
                      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#C9A961] to-[#8B6F47] flex items-center justify-center">
                            <span className="font-serif text-white text-sm tracking-wider uppercase">{activeBrand().igHandle.charAt(0)}</span>
                          </div>
                          <div>
                            <div className="font-sans text-[13px] font-semibold text-[#1A3B5C]">{activeBrand().igHandle}</div>
                            <div className="font-sans text-[10px] text-gray-500">{activeBrand().igLocation} · Sponsored</div>
                          </div>

                        </div>
                        <MoreHorizontal className="w-5 h-5 text-gray-500" />
                      </div>

                      <div className="relative aspect-square bg-gray-100">
                        {p.video_url ? (
                          <video src={p.video_url} className="w-full h-full object-cover" muted loop playsInline />
                        ) : p.image_url ? (
                          <img src={p.image_url} alt={p.theme || ''} className="w-full h-full object-cover" />
                        ) : (
                          <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-300 font-sans text-xs p-4 text-center">
                            <ImageIcon className="w-7 h-7 mb-2" />
                            {p.image_prompt || 'No image yet'}
                          </div>
                        )}

                        {p.post_type === 'Reel' && (
                          <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm rounded-md px-2 py-1 text-white text-[10px] tracking-wider font-sans">
                            REEL
                          </div>
                        )}
                        {p.post_type === 'Carousel' && (
                          <div className="absolute top-3 right-3 flex gap-1">
                            {[1, 2, 3].map((d) => (
                              <span key={d} className="w-1.5 h-1.5 rounded-full bg-white/80" />
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="px-4 pt-3 pb-2 flex items-center justify-between border-t border-gray-100">
                        <div className="flex items-center gap-4">
                          <Heart className="w-6 h-6 text-gray-800" />
                          <MessageCircle className="w-6 h-6 text-gray-800" />
                          <Send className="w-6 h-6 text-gray-800" />
                        </div>
                        <Bookmark className="w-6 h-6 text-gray-800" />
                      </div>

                      <div className="px-4 pb-4">
                        <div className="font-sans text-[13px] font-semibold text-[#1A3B5C] mb-1">
                          {(1200 + index * 247).toLocaleString()} likes
                        </div>
                        <div className="font-sans text-[13px] text-gray-800 leading-relaxed whitespace-pre-line">
                          <span className="font-semibold text-[#1A3B5C]">{activeBrand().igHandle}</span> {captionFirstLine}

                          {(p.caption || '').includes('\n') && <button className="text-gray-500 ml-1">... more</button>}
                        </div>
                        <div className="font-sans text-[10px] text-gray-400 tracking-wider mt-2 uppercase">
                          {fmtDateTime(p.scheduled_for)}
                        </div>
                      </div>
                    </div>
                    <p className="text-center font-sans text-[10px] text-slate-400 mt-3 tracking-wider uppercase">
                      Post preview — exactly as it will appear on Instagram
                    </p>
                  </div>

                  {/* Review panel */}
                  <div className="space-y-6">
                    {p.theme && (
                      <div className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#C9A961]">
                        Theme · <span className="text-[#8B6F47]">{p.theme}</span>
                      </div>
                    )}

                    <p className="font-sans text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                      {p.caption || <span className="italic text-slate-400">No caption</span>}
                    </p>

                    {p.hashtags && p.hashtags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {p.hashtags.map((h, i) => (
                          <span
                            key={`${h}-${i}`}
                            className="font-sans text-[11px] text-[#1A3B5C] bg-[#F3EFE6] border border-[#C9A961]/30 px-2.5 py-1 rounded-full inline-flex items-center gap-1"
                          >
                            <Hash className="w-2.5 h-2.5" />
                            {h.replace(/^#/, '')}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Existing feedback (for already-reviewed posts) */}
                    {p.admin_feedback && st !== 'pending' && (
                      <div
                        className={`flex items-start gap-2 border-l-2 pl-3 py-1 ${
                          st === 'rejected' ? 'border-red-400' : 'border-emerald-400'
                        }`}
                      >
                        <MessageSquare className="w-3.5 h-3.5 mt-0.5 text-slate-400 flex-shrink-0" />
                        <div>
                          <div className="font-sans text-[10px] tracking-wider uppercase text-slate-400">
                            Admin feedback
                          </div>
                          <p className="font-sans text-xs text-slate-700 mt-0.5">{p.admin_feedback}</p>
                        </div>
                      </div>
                    )}

                    {st === 'rejected' && (
                      <div className="flex items-center gap-2 text-amber-700">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        <span className="font-sans text-[11px]">
                          Sent back to Step 3 (Draft) for revision.
                        </span>
                        <button
                          onClick={() => goToStep(3)}
                          className="font-sans text-[10px] tracking-wider uppercase text-[#C9A961] hover:underline flex items-center gap-0.5"
                        >
                          Open Step 3 <ChevronRight className="w-3 h-3" />
                        </button>
                      </div>
                    )}

                    {/* Review controls */}
                    <div className="pt-3 border-t border-slate-100">
                      <textarea
                        value={feedbackDraft[p.id] ?? (st !== 'pending' ? p.admin_feedback || '' : '')}
                        onChange={(e) => setFeedbackDraft({ ...feedbackDraft, [p.id]: e.target.value })}
                        placeholder="Feedback for the team (required to disapprove, optional to approve)…"
                        className="w-full border border-slate-200 px-3 py-2 font-sans text-sm h-16 resize-none bg-white text-[#0D1B2A] placeholder:text-slate-400 focus:border-[#C9A961] outline-none"
                      />
                      <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-2">
                        <button
                          onClick={() => handleApprove(p)}
                          disabled={busy}
                          className="font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-3 bg-emerald-600 text-white hover:bg-emerald-700 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-60"
                        >
                          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ThumbsUp className="w-3.5 h-3.5" />}
                          Approve
                        </button>
                        <button
                          onClick={() => handleReject(p)}
                          disabled={busy}
                          className="font-sans text-[10px] tracking-[0.2em] uppercase px-4 py-3 border border-red-300 text-red-600 hover:bg-red-50 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-60"
                        >
                          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ThumbsDown className="w-3.5 h-3.5" />}
                          Disapprove & Send Back
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ApprovalStep;
